import { Button, Card, Descriptions, Modal, Table } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import LOCALS from 'commons/locals';
import { ProductUpdateInfoNewType } from 'types/pms';
import i18n from 'i18n';
import { History } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import dayjs from 'dayjs';
import JSONViewer from 'components/simple-json-viewer';

type ProductActivity = NonNullable<
  ProductUpdateInfoNewType['pmsProductActivityList']
>[number];

const hasActivityDetail = (actDetail?: string) => !!actDetail?.trim();

const ProductActivityList = ({
  id,
  pmsProductActivityList,
}: {
  id?: ProductUpdateInfoNewType['id'];
  pmsProductActivityList?: ProductUpdateInfoNewType['pmsProductActivityList'];
}) => {
  const [selectedActivity, setSelectedActivity] =
    useState<ProductActivity | null>(null);

  const activityList = useMemo(
    () => [...(pmsProductActivityList || [])].reverse(),
    [pmsProductActivityList],
  );

  const activityListWithDetail = useMemo(
    () => activityList.filter((item) => hasActivityDetail(item.actDetail)),
    [activityList],
  );

  const selectedIndex = useMemo(() => {
    if (!selectedActivity) return -1;
    return activityListWithDetail.findIndex(
      (item) => item.actId === selectedActivity.actId,
    );
  }, [selectedActivity, activityListWithDetail]);

  const canGoPrev = selectedIndex > 0;
  const canGoNext =
    selectedIndex >= 0 && selectedIndex < activityListWithDetail.length - 1;

  const columns = useMemo(() => {
    return [
      {
        title: <Trans i18nKey={LOCALS.operator} />,
        dataIndex: 'actUsername',
        key: 'actUsername',
      },
      {
        title: <Trans i18nKey={LOCALS.operation_time} />,
        dataIndex: 'actCreateAt',
        key: 'actCreateAt',
        render: (actCreateAt: string) =>
          actCreateAt ? dayjs(actCreateAt).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
      {
        title: <Trans i18nKey={LOCALS.option} />,
        dataIndex: 'actAction',
        key: 'actAction',
        render: (
          actAction: string,
          {
            actDetail,
          }: {
            actDetail?: string;
          },
        ) => {
          if (!actDetail) {
            return <div>{actAction}</div>;
          }

          try {
            const detailObj = JSON.parse(actDetail);

            if (!detailObj.price) {
              return <div>{actAction}</div>;
            }

            return (
              <div>
                <span>{actAction}</span>
                <span className="text-red-700">
                  {!!detailObj.price?.from && (
                    <span className="ml-4">
                      price from {detailObj.price?.from.toLocaleString()}
                    </span>
                  )}
                  {!!detailObj.price?.to && (
                    <span className="ml-4">
                      price to {detailObj.price?.to.toLocaleString()}
                    </span>
                  )}
                </span>
              </div>
            );
          } catch (err) {
            return <div>{actAction}</div>;
          }
        },
      },
      {
        title: <Trans i18nKey={LOCALS.details} />,
        dataIndex: 'actDetail',
        key: 'actDetail',
        render: (actDetail: string, record: ProductActivity) => {
          if (!hasActivityDetail(actDetail)) {
            return '-';
          }

          return (
            <Button
              onClick={() => {
                setSelectedActivity(record);
              }}
            >
              {i18n.t(LOCALS.details)}
            </Button>
          );
        },
      },
    ];
  }, []);

  return !!id ? (
    <Card bodyStyle={{ padding: '16px' }}>
      <Modal
        title={i18n.t(LOCALS.details)}
        open={!!selectedActivity}
        onCancel={() => {
          setSelectedActivity(null);
        }}
        footer={null}
        width="1200px"
      >
        {selectedActivity && (
          <>
            {activityListWithDetail.length > 1 && (
              <div className="mb-4 flex items-center justify-center gap-3">
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  disabled={!canGoPrev}
                  onClick={() => {
                    if (canGoPrev) {
                      setSelectedActivity(
                        activityListWithDetail[selectedIndex - 1],
                      );
                    }
                  }}
                />
                <span className="text-sm text-gray-500">
                  {selectedIndex + 1} / {activityListWithDetail.length}
                </span>
                <Button
                  type="text"
                  icon={<RightOutlined />}
                  disabled={!canGoNext}
                  onClick={() => {
                    if (canGoNext) {
                      setSelectedActivity(
                        activityListWithDetail[selectedIndex + 1],
                      );
                    }
                  }}
                />
              </div>
            )}
            <Descriptions
              bordered
              size="small"
              column={1}
              className="mb-4"
              items={[
                {
                  key: 'actUsername',
                  label: i18n.t(LOCALS.operator),
                  children: selectedActivity.actUsername || '-',
                },
                {
                  key: 'actCreateAt',
                  label: i18n.t(LOCALS.operation_time),
                  children: selectedActivity.actCreateAt
                    ? dayjs(selectedActivity.actCreateAt).format(
                        'YYYY-MM-DD HH:mm:ss',
                      )
                    : '-',
                },
                {
                  key: 'actAction',
                  label: i18n.t(LOCALS.option),
                  children: selectedActivity.actAction || '-',
                },
              ]}
            />
            <JSONViewer data={JSON.parse(selectedActivity.actDetail)} />
          </>
        )}
      </Modal>
      <div className="flex items-center gap-2 mb-4">
        <History className="w-5 h-5" />
        <div className="text-base font-medium">
          {i18n.t(LOCALS.operate_histories)}
        </div>
      </div>
      <Table
        size="small"
        bordered
        pagination={false}
        columns={columns}
        rowKey={'actId'}
        scroll={{ x: 'max-content' }}
        tableLayout="auto"
        dataSource={activityList}
      />
    </Card>
  ) : null;
};

export default ProductActivityList;
